import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const SITUATIONS = [
  { id: "short-sales", label: "I owe more than the home is worth", contact: "Short sale" },
  { id: "foreclosure", label: "I'm behind on payments", contact: "Foreclosure concerns" },
  { id: "inherited-vacant", label: "I inherited a home, or it's sitting empty", contact: "Inherited or vacant property" },
  { id: "liens", label: "There are liens or code fines", contact: "Liens or judgments" },
  { id: "title-issues", label: "There's a problem with the title", contact: "Title issues" },
  { id: "other", label: "Something else entirely", contact: "Something else / not sure" },
];

const TIMING = [
  { id: "weeks", label: "It feels urgent — weeks, not months" },
  { id: "months", label: "Soon — within a few months" },
  { id: "exploring", label: "I'm just exploring quietly" },
];

const NOTES = {
  "short-sales": { title: "Short Sale Guidance", note: "A short sale may let you close this chapter with far less damage than foreclosure — and we handle the lender conversations for you." },
  "foreclosure": { title: "Foreclosure Support", note: "Florida's court process usually leaves a window to act. The sooner we look together, the more options you'll have." },
  "inherited-vacant": { title: "Inherited & Vacant Property Guidance", note: "Probate, heirs, and upkeep can be untangled respectfully — even if you live out of state." },
  "liens": { title: "Lien & Judgment Resolution", note: "Liens often settle for less than the recorded amount. We'll find exactly what's attached and what's negotiable." },
  "title-issues": { title: "Title Issue Navigation", note: "Clouds on title can usually be cured with the right paperwork — we coordinate it with trusted title professionals." },
  "other": { title: "Complex & Creative Situations", note: "If it doesn't fit a box, that's our specialty. One short conversation is all it takes to see your paths." },
};

const urgencyNote = (t) =>
  t === "weeks"
    ? "Because your timeline is tight, we'd suggest talking this week — even a 10-minute call can protect your options."
    : t === "months"
    ? "You have room to breathe — and starting early keeps every option on the table."
    : "Exploring quietly is exactly the right first step. No one will know you reached out.";

const optionBtn =
  "w-full text-left rounded-full border border-sage/30 bg-ivory px-7 py-4 text-night hover:border-sage-deep hover:bg-sage-pale hover:translate-x-1 transition-all duration-300";

export default function SituationQuiz() {
  const [step, setStep] = useState(0);
  const [situation, setSituation] = useState(null);
  const [timing, setTiming] = useState(null);

  const reset = () => {
    setStep(0);
    setSituation(null);
    setTiming(null);
  };

  const result = situation ? NOTES[situation.id] : null;

  return (
    <section data-testid="situation-quiz" className="py-28 lg:py-36 bg-coastal-pale grain">
      <div className="relative z-[2] mx-auto max-w-3xl px-6 lg:px-12">
        <Reveal className="text-center">
          <SectionLabel>Not sure where you fit?</SectionLabel>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night">
            Two quiet questions, one clear direction.
          </h2>
        </Reveal>

        <Reveal className="mt-14" delay={0.1}>
          <div className="bg-ivory/70 backdrop-blur-sm rounded-[2rem] p-8 sm:p-12 shadow-[0_30px_70px_-25px_rgba(26,36,33,0.15)]">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div key="q1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
                  <p className="font-serif text-2xl text-night mb-8" data-testid="quiz-question-1">What's weighing on you about the property?</p>
                  <div className="space-y-3">
                    {SITUATIONS.map((s) => (
                      <button key={s.id} data-testid={`quiz-option-${s.id}`} className={optionBtn} onClick={() => { setSituation(s); setStep(1); }}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div key="q2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
                  <p className="font-serif text-2xl text-night mb-8" data-testid="quiz-question-2">And how soon does it need attention?</p>
                  <div className="space-y-3">
                    {TIMING.map((t) => (
                      <button key={t.id} data-testid={`quiz-timing-${t.id}`} className={optionBtn} onClick={() => { setTiming(t.id); setStep(2); }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <button data-testid="quiz-back-button" onClick={() => setStep(0)} className="mt-8 inline-flex items-center gap-2 text-sm text-charcoal hover:text-sage-deep transition-colors duration-300">
                    <ArrowLeft size={14} strokeWidth={2} /> Back
                  </button>
                </motion.div>
              )}

              {step === 2 && result && (
                <motion.div key="result" data-testid="quiz-result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="text-center">
                  <p className="text-[11px] tracking-[0.24em] uppercase font-semibold text-sage-deep">The place to start</p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-night mt-4" data-testid="quiz-result-title">{result.title}</h3>
                  <p className="mt-5 text-charcoal leading-relaxed max-w-xl mx-auto">{result.note}</p>
                  <p className="mt-4 text-sm text-charcoal/80 italic max-w-xl mx-auto">{urgencyNote(timing)}</p>
                  <div className="mt-9 flex flex-wrap justify-center gap-4">
                    <Link
                      to={`/contact?situation=${encodeURIComponent(situation.contact)}`}
                      data-testid="quiz-cta-contact"
                      className="rounded-full bg-night text-ivory px-8 py-4 text-sm tracking-wide hover:bg-sage-deep hover:scale-[1.03] transition-all duration-300 flex items-center gap-2"
                    >
                      Talk it through privately <ArrowRight size={15} strokeWidth={1.75} />
                    </Link>
                    <Link
                      to={`/services#${situation.id}`}
                      data-testid="quiz-cta-service"
                      className="rounded-full border border-night/20 text-night px-8 py-4 text-sm tracking-wide hover:border-sage-deep hover:text-sage-deep transition-all duration-300"
                    >
                      Read how we help
                    </Link>
                  </div>
                  <button data-testid="quiz-restart-button" onClick={reset} className="mt-8 inline-flex items-center gap-2 text-sm text-charcoal hover:text-sage-deep transition-colors duration-300">
                    <RotateCcw size={13} strokeWidth={2} /> Start over
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
